import React from 'react';
import {
  getYearRange,
  getQuarterRange,
  getMonthRange,
  isSameRange,
  getCurrentYear,
  getCurrentQuarter,
} from './utils/dateRangeUtils';
import { YEARS, QUARTERS, MONTHS } from './constants';
import type { DateRangeValue } from './DateRangeInput';

const YearColumn: React.FC<{ selectedYear: number; onYearSelect: (year: number) => void }> = ({ selectedYear, onYearSelect }) => (
  <div className="w-1/3 overflow-y-auto p-2 space-y-1">
    {YEARS.map((year) => (
      <button
        key={year}
        onClick={() => onYearSelect(year)}
        className={`
          w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between
          ${selectedYear === year ? 'bg-primary/10 text-primary border border-primary/20 font-medium' : 'text-foreground hover:bg-superliteprimary'}
        `}
      >
        <span>{year}</span>
      </button>
    ))}
  </div>
);

export const YearTabPanel: React.FC<{ value: DateRangeValue; onApplyYear: (year: number) => void }> = ({ value, onApplyYear }) => (
  <div className="grid grid-cols-1 gap-1 p-4 overflow-y-auto">
    {YEARS.map((year) => {
      const isActive = isSameRange(value, getYearRange(year));
      return (
        <button
          key={year}
          onClick={() => onApplyYear(year)}
          className={`
            text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between
            ${isActive ? 'bg-primary/10 text-primary border border-primary/20 font-medium' : 'text-foreground hover:bg-superliteprimary hover:text-primary'}
          `}
        >
          <span>{year}</span>
        </button>
      );
    })}
  </div>
);

export const QuarterTabPanel: React.FC<{
  value: DateRangeValue;
  selectedYear: number;
  onYearSelect: (year: number) => void;
  onApplyQuarter: (year: number, quarter: number) => void;
}> = ({
  value,
  selectedYear,
  onYearSelect,
  onApplyQuarter,
}) => (
  <div className="flex h-full divide-x divide-border">
    <YearColumn selectedYear={selectedYear} onYearSelect={onYearSelect} />
    <div className="w-2/3 overflow-y-auto p-2 space-y-1">
      {QUARTERS.map((quarter) => {
        const isActive = isSameRange(value, getQuarterRange(selectedYear, quarter.value));
        return (
          <button
            key={quarter.value}
            onClick={() => onApplyQuarter(selectedYear, quarter.value)}
            className={`
              w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between
              ${isActive ? 'bg-primary/10 text-primary border border-primary/20 font-medium' : 'text-foreground hover:bg-superliteprimary'}
            `}
          >
            <span>{quarter.label}</span>
            {quarter.value === getCurrentQuarter() && selectedYear === getCurrentYear() && (
              <span className="text-[10px] uppercase font-bold opacity-80">Huidig kwartaal</span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

export const MonthTabPanel: React.FC<{
  value: DateRangeValue;
  selectedYear: number;
  onYearSelect: (year: number) => void;
  onApplyMonth: (year: number, month: number) => void;
}> = ({
  value,
  selectedYear,
  onYearSelect,
  onApplyMonth,
}) => (
  <div className="flex h-full divide-x divide-border">
    <YearColumn selectedYear={selectedYear} onYearSelect={onYearSelect} />
    <div className="w-2/3 overflow-y-auto p-2 space-y-1">
      <div className="grid grid-cols-1 gap-1">
        {MONTHS.map((monthOption) => {
          const isActive = isSameRange(value, getMonthRange(selectedYear, monthOption.index));
          return (
            <button
              key={monthOption.index}
              onClick={() => onApplyMonth(selectedYear, monthOption.index)}
              className={`
                text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between
                ${isActive ? 'bg-primary/10 text-primary border border-primary/20 font-medium' : 'text-foreground hover:bg-superliteprimary'}
              `}
            >
              <span>{monthOption.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

export const CustomTabPanel: React.FC<{
  fromInputValue: string;
  toInputValue: string;
  onFromInputChange: (value: string) => void;
  onToInputChange: (value: string) => void;
  onFromInputBlur: (value: string) => void;
  onToInputBlur: (value: string) => void;
}> = ({
  fromInputValue,
  toInputValue,
  onFromInputChange,
  onToInputChange,
  onFromInputBlur,
  onToInputBlur,
}) => (
  <div className="p-4 space-y-4">
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground uppercase">Van</label>
      <input
        type="date"
        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        value={fromInputValue}
        onChange={(e) => onFromInputChange(e.target.value)}
        onBlur={(e) => onFromInputBlur(e.target.value)}
      />
    </div>
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground uppercase">Tot</label>
      <input
        type="date"
        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        value={toInputValue}
        onChange={(e) => onToInputChange(e.target.value)}
        onBlur={(e) => onToInputBlur(e.target.value)}
      />
    </div>
  </div>
);
